import { useMemo } from 'react'
import { createEntityHooks } from '../core/createEntityHooks'
import { useLabels } from './labels'
import type { BaseDocument } from '../core/types'

/**
 * TaskLabel document interface
 */
export interface TaskLabelDocument extends BaseDocument {
  taskId: string
  labelId: string
  labelGroupId?: string | null
  labelName: string
  labelColor: string
  labelGroupName?: string | null
}

/**
 * Enriched TaskLabel with fresh label data from join
 */
export interface EnrichedTaskLabel extends TaskLabelDocument {
  label?: {
    id: string
    name: string
    color: string
    description: string | null
    labelGroupId: string | null
  }
}

/**
 * TaskLabel-specific hooks
 */
const hooks = createEntityHooks<TaskLabelDocument>('tasklabels')

/**
 * Get task labels with fresh label data joined from labels collection.
 * Falls back to denormalized fields if label not found (offline resilience).
 */
export const useTaskLabels = (filter?: Partial<TaskLabelDocument>) => {
  const { data, isLoading, db } = hooks.useEntities(filter)
  const { labels } = useLabels()

  const enrichedTaskLabels = useMemo<Array<EnrichedTaskLabel>>(() => {
    if (!data) return []

    return data.map((taskLabel) => {
      const label = labels?.find((l) => l.id === taskLabel.labelId && !l.isDeleted)
      return {
        ...taskLabel,
        label: label ? {
          id: label.id,
          name: label.name,
          color: label.color,
          description: label.description,
          labelGroupId: label.labelGroupId,
        } : undefined,
        // Keep denormalized fields as fallback for offline scenarios
      }
    })
  }, [data, labels])

  return { taskLabels: enrichedTaskLabels, isLoading, db }
}

export const useTaskLabel = (id: string | undefined) => {
  const { data, isLoading } = hooks.useEntity(id)
  return { taskLabel: data, isLoading }
}

export const useTaskLabelMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createTaskLabel: create,
    updateTaskLabel: update,
    deleteTaskLabel: deleteEntity,
  }
}
